'use client'

// Súper online PR 4 (Tarea 6): los hooks del carrito compartido entre páginas. La lógica
// vive en lib/storefront/groceryCart.ts (pura, con sus tests); acá solo se cablea a
// efectos y eventos del navegador.
//
//   - `usePersistedGroceryCart`: la portada (`RestaurantMicrosite`), dueña de su estado
//     `cart`. Con `enabled: false` (todo restaurante) no lee ni escribe el storage, no
//     escucha eventos y nunca llama `setCart`.
//   - `useGroceryCart`: las páginas de pasillo y de producto. `add` relee lo guardado y
//     agrega encima, nunca sobre un estado de React viejo.
//
// Las dos resincronizan al volver: `pageshow` (también desde el bfcache, `persisted`),
// `visibilitychange` a visible y el evento `storage` de la misma llave (otra pestaña).
import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import type { CartItem } from '../types'
import {
  addToGroceryCart,
  groceryCartKey,
  makeGroceryCartSync,
  readGroceryCart,
  type CartLineFacts,
  type GroceryCartSync,
} from '@/lib/storefront/groceryCart'

const NO_FACTS: Record<string, CartLineFacts> = {}
const NO_CART: CartItem[] = []

/**
 * «Agregar» en esta misma pestaña (Tarea 7). El evento `storage` solo llega a las OTRAS
 * pestañas: sin este aviso, la barra del carrito del pasillo (otra instancia del hook) no se
 * enteraría de lo que acaba de agregar la tarjeta de al lado. `detail` = slug de la tienda.
 */
const GROCERY_CART_ADDED = 'redy:grocery-cart-added'

/** Llama `onReturn` cada vez que el cliente vuelve a esta página o otra pestaña cambia el carrito. */
function onPageReturn(slug: string, onReturn: () => void): () => void {
  const key = groceryCartKey(slug)
  const onShow = () => onReturn()
  const onVisible = () => {
    if (document.visibilityState === 'visible') onReturn()
  }
  const onStorage = (e: StorageEvent) => {
    if (e.key === key || e.key === null) onReturn()
  }
  window.addEventListener('pageshow', onShow)
  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener('pageshow', onShow)
    document.removeEventListener('visibilitychange', onVisible)
    window.removeEventListener('storage', onStorage)
  }
}

export function usePersistedGroceryCart(args: {
  slug: string
  enabled: boolean
  cart: CartItem[]
  setCart: Dispatch<SetStateAction<CartItem[]>>
  catalog: ReadonlyArray<CartLineFacts>
  /** El cliente volvió de pagar (`#pago=<token>`, `shouldClearOnPaymentReturn`), leído en el
   *  primer render, antes de que `usePaymentReturn` limpie el fragmento: lo guardado es la
   *  orden pagada y se borra en vez de hidratarse. */
  paidReturn?: boolean
}): {
  facts: Record<string, CartLineFacts>
  /** true tras la primera hidratación (en ese render `cart` ya es el hidratado). Siempre false sin la bandera. */
  hydrated: boolean
  updateFacts(fresh: Record<string, Partial<CartLineFacts>>): void
  confirmOrder(): void
} {
  const { slug, enabled, cart, setCart, catalog, paidReturn = false } = args
  // El controlador se crea UNA vez por montaje (la tienda y la bandera no cambian en la
  // página). Con el inicializador de useState y no con un useRef: `react-hooks/refs` no deja
  // leer `ref.current` durante el render.
  const [sync] = useState<GroceryCartSync>(() => makeGroceryCartSync({ slug, enabled }))
  const [facts, setFacts] = useState<Record<string, CartLineFacts>>(NO_FACTS)
  const [hydrated, setHydrated] = useState(false)
  // El arreglo del primer render (el `[]` de useState). Mientras `cart` siga siendo ése no
  // hay nada que guardar, y persistirlo podría pisar lo guardado: React puede calcular el
  // updater de la hidratación en el acto (o dos veces en StrictMode) y dejar al controlador
  // hidratado mientras este efecto todavía ve el carrito vacío del primer render.
  const initialCartRef = useRef(cart)

  // Persistir en cada cambio. Declarado ANTES que la hidratación: en el montaje corre
  // primero y el controlador lo salta (todavía no hidrató).
  useEffect(() => {
    if (!enabled || cart === initialCartRef.current) return
    const r = sync.persist(cart, catalog)
    if (r.kind === 'stale') {
      // Otra página guardó después: gana lo guardado, no se pisa. Es el storage (un
      // sistema externo) el que manda acá, no un estado derivado.
      setCart(r.cart)
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFacts(sync.facts())
    }
  }, [enabled, sync, cart, catalog, setCart])

  // Hidratar al montar. Corre después del de `usePaymentReturn` (llamado antes en la
  // portada): si la vuelta del pago ya restauró un carrito, `hydrate` lo deja ganar.
  useEffect(() => {
    if (!enabled) return
    // Vuelta del pago de paso: borrar lo guardado ANTES de hidratar (revisión T6, #2). Si
    // el sondeo de la confirmación tarda o se rinde, lo pagado no reaparece. Idempotente
    // (StrictMode, y `showConfirmation` lo vuelve a llamar).
    if (paidReturn) sync.confirmOrder()
    setCart((prev) => sync.hydrate(prev))
    // El updater de `cart` (declarado antes en la portada) corre antes que éste. Se hidrata
    // en un efecto, no en el render: el servidor no tiene storage y el HTML no cambiaría.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFacts(() => sync.facts())
    setHydrated(true)
  }, [enabled, sync, setCart, paidReturn])

  // Volver a la página (bfcache, pestaña, otra pestaña): traer lo que otra página guardó.
  useEffect(() => {
    if (!enabled) return
    return onPageReturn(slug, () => {
      const next = sync.resync()
      if (next) {
        setCart(next)
        setFacts(sync.facts())
      }
    })
  }, [enabled, slug, sync, setCart])

  const updateFacts = useCallback(
    (fresh: Record<string, Partial<CartLineFacts>>) => {
      if (!enabled) return
      sync.updateFacts(fresh)
      setFacts(sync.facts())
    },
    [enabled, sync],
  )

  const confirmOrder = useCallback(() => {
    if (!enabled) return
    sync.confirmOrder()
    setFacts(NO_FACTS)
  }, [enabled, sync])

  return { facts, hydrated, updateFacts, confirmOrder }
}

/** Pasillo y producto: el carrito guardado de la tienda, y `add` que agrega encima de lo guardado. */
export function useGroceryCart(slug: string): {
  cart: CartItem[]
  facts: Record<string, CartLineFacts>
  add(line: CartItem, facts: CartLineFacts): void
} {
  const [state, setState] = useState<{ cart: CartItem[]; facts: Record<string, CartLineFacts> }>({
    cart: NO_CART,
    facts: NO_FACTS,
  })

  useEffect(() => {
    const load = () => setState(readGroceryCart(slug))
    load()
    const onAdded = (e: Event) => {
      if ((e as CustomEvent<string>).detail === slug) load()
    }
    window.addEventListener(GROCERY_CART_ADDED, onAdded)
    const offReturn = onPageReturn(slug, load)
    return () => {
      offReturn()
      window.removeEventListener(GROCERY_CART_ADDED, onAdded)
    }
  }, [slug])

  const add = useCallback(
    (line: CartItem, facts: CartLineFacts) => {
      setState(addToGroceryCart(slug, line, facts))
      window.dispatchEvent(new CustomEvent(GROCERY_CART_ADDED, { detail: slug }))
    },
    [slug],
  )

  return { cart: state.cart, facts: state.facts, add }
}
